import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { serverThrowSchema } from '../schema/errorDemoSchema';
import type { ServerThrowFormValues } from '../schema/errorDemoSchema';
import type { ErrorCodeNode } from '../types/errorDemo';
import { useServerThrow } from '../hooks/useThrowDemo';
import { ThrowResultCard } from './ThrowResultCard';

interface ServerThrowPanelProps {
  selectedNode: ErrorCodeNode | null;
}

const SIMULATE_OPTIONS = [
  { value: 'internal', label: 'internal (COMMON-500-001)' },
  { value: 'null-pointer', label: 'null-pointer (RuntimeException)' },
  { value: 'illegal-state', label: 'illegal-state (IllegalStateException)' },
] as const;

/**
 * Panel for POST /api/eraf/error-demo/simulate/server (R8-E-001: was /throw/server).
 * Demonstrates 5xx via GlobalExceptionHandler.handleAll.
 */
export function ServerThrowPanel({ selectedNode }: ServerThrowPanelProps) {
  const { t } = useTranslation();
  const { result, throwServer, reset } = useServerThrow();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ServerThrowFormValues>({
    resolver: zodResolver(serverThrowSchema),
    defaultValues: { simulate: 'internal' },
  });

  // Reset result when node changes
  useEffect(() => {
    if (selectedNode) {
      reset();
    }
  }, [selectedNode, reset]);

  const onSubmit = async (values: ServerThrowFormValues) => {
    await throwServer({ simulate: values.simulate });
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}
      aria-label={t('errorDemo.server.title')}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          Server throw
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            padding: '2px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          POST /simulate/server
        </span>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label={t('errorDemo.server.title')}>
        {/* simulate select */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="server-simulate"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            {t('errorDemo.server.simulateLabel')}
          </label>
          <select
            id="server-simulate"
            {...register('simulate')}
            aria-describedby={errors.simulate ? 'server-simulate-error' : undefined}
            aria-invalid={!!errors.simulate}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${errors.simulate ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            {SIMULATE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.simulate && (
            <span
              id="server-simulate-error"
              role="alert"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {errors.simulate.message}
            </span>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting || result.status === 'loading'}
          aria-busy={isSubmitting || result.status === 'loading'}
          style={{
            padding: 'var(--spacing-2) var(--spacing-6)',
            background:
              isSubmitting || result.status === 'loading'
                ? 'var(--color-surface-elevated)'
                : 'var(--color-brand-primary)',
            color:
              isSubmitting || result.status === 'loading'
                ? 'var(--color-text-muted)'
                : 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            cursor: isSubmitting || result.status === 'loading' ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          {(isSubmitting || result.status === 'loading') && (
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                border: '2px solid currentColor',
                borderTopColor: 'transparent',
                borderRadius: 'var(--radius-full)',
                animation: 'spin 0.75s linear infinite',
              }}
            />
          )}
          throw
        </button>
      </form>

      {/* Hint */}
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
        {t('errorDemo.server.hint')}
      </p>

      {/* Result */}
      <ThrowResultCard result={result} />
    </div>
  );
}
